import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import publicApiClient from '@services/api/publicClient'
import likesCommentsService from '@services/api/likesComments'
import apiClient from '@services/api/client'
import { useAuthStore } from '@store/authStore'
import toast from 'react-hot-toast'
import avatarPlaceholder from '@assets/images/image_01.png'
import { storageURL, mediaUrl } from '@config'
import { encodeId, decodeId } from '@utils/idCipher'

const CONDITION_LABELS = {
  neuf: 'Neuf', scelle: 'Scellé', reconditionne: 'Reconditionné',
  quasi_neuf: 'Quasi neuf', excellent: 'Excellent', bon: 'Bon état',
  moyen: 'État moyen', usage: 'Usagé',
}

// Prix affiché (deal) + ancien prix (barré). Gère la convention actuelle
// (prix=original, new_price=prix réduit, marge_reduite=%) ET l'ancienne (new_price=ancien prix).
const resolvePricing = (raw) => {
  const prixNum     = parseFloat(raw?.prix) || 0
  const newPriceNum = parseFloat(raw?.new_price) || 0
  const marge       = parseInt(String(raw?.marge_reduite ?? '').replace(/[^0-9]/g, '')) || 0
  if (marge > 0 && newPriceNum > 0 && newPriceNum < prixNum) {
    return { price: newPriceNum, oldPrice: prixNum }
  }
  if (newPriceNum > prixNum && newPriceNum > 0) {
    return { price: prixNum, oldPrice: newPriceNum }
  }
  return { price: prixNum, oldPrice: null }
}

function ProductDetailPage() {
  const { id: encodedId } = useParams()
  const id = decodeId(encodedId) // id réel utilisé pour les appels API
  const navigate = useNavigate()
  const { user, isAuthenticated } = useAuthStore()
  const [product, setProduct] = useState(null)
  const [loading, setLoading] = useState(true)
  const [selectedImage, setSelectedImage] = useState(0)
  const [relatedProducts, setRelatedProducts] = useState([])
  const [isLiked, setIsLiked] = useState(false)
  const [likes, setLikes] = useState(0)
  const [comments, setComments] = useState([])
  const [newComment, setNewComment] = useState('')
  const [detailTab, setDetailTab] = useState('description')
  const [loadingComments, setLoadingComments] = useState(false)
  const [isSubscribed, setIsSubscribed] = useState(false)
  const incrementedIdRef = useRef(null)
  const [subscribersCount, setSubscribersCount] = useState(0)
  const [relatedPage, setRelatedPage] = useState(0)
  const [perView, setPerView] = useState(5)

  useEffect(() => {
    const update = () => {
      if (window.innerWidth >= 1024) setPerView(5)
      else if (window.innerWidth >= 640) setPerView(3)
      else setPerView(2)
    }
    update()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [])

  useEffect(() => { setRelatedPage(0) }, [perView])

  // Fonction pour formater les prix en FCFA
  const formatPrice = (price) => {
    const priceNum = typeof price === 'string' ? parseFloat(price.replace(/[^0-9]/g, '')) : price
    if (!priceNum || priceNum === 0) return ''
    return new Intl.NumberFormat('fr-FR').format(priceNum) + ' FCFA'
  }

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true)
        const res = await publicApiClient.get(`/posts/${id}`)
        const raw = res.data || res

        // Normaliser les champs backend → frontend
        const conditionKey = raw.etat_appareil || ''

        const pricing = resolvePricing(raw)
        const productData = {
          id:                  raw.post_id,
          name:                raw.libele,
          price:               pricing.price,
          oldPrice:            pricing.oldPrice,
          description:         raw.description,
          location:            raw.situationGeographique || raw.siteWeb,
          category_id:         raw.post_categorie_id,
          views:               raw.vues || 0,
          likes:               raw.post_likes_count || raw.likes || 0,
          etat_appareil:       conditionKey,
          etat_appareil_label: CONDITION_LABELS[conditionKey] || '',
          date:                raw.created_at ? new Date(raw.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '',
          // Caractéristiques renseignées selon la catégorie (véhicule, immobilier…)
          specs: [
            ['Marque', raw.marque],
            ['Modèle', raw.model],
            ['Année', raw.annee_vehicule],
            ['Kilométrage', raw.kilometrage ? `${raw.kilometrage} km` : null],
            ['Carburant', raw.carburant],
            ['Transmission', raw.transmission],
            ['Pièces', raw.nombrePieceMaison],
            ['Salles de bain', raw.nombreSalleBain],
            ['Superficie', raw.Superficie ? `${raw.Superficie} m²` : null],
            ['Entreprise', raw.nom_entreprise],
          ].filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== ''),
          images:              raw.fichiers?.map(f => mediaUrl(f.chemin)) || [],
          image:               (() => { const f = raw.fichiers?.find(f => f.is_main) ?? raw.fichiers?.[0]; return f?.chemin ? mediaUrl(f.chemin) : null })(),
          client: raw.client ? {
            id:    raw.client.client_id,
            name:  [raw.client.nom, raw.client.prenom].filter(Boolean).join(' '),
            phone: raw.client.numero,
            photo: raw.client.photo_profil
                     ? (raw.client.photo_profil.startsWith('http') ? raw.client.photo_profil : mediaUrl(raw.client.photo_profil))
                     : null,
          } : null,
        }

        setProduct(productData)
        setLikes(productData.likes)

        // Appels nécessitant une connexion
        if (user?.client_id || user?.id) {
          const clientId = user?.id || user?.client_id

          // Vérifier si l'utilisateur a déjà liké
          try {
            const likeStatus = await likesCommentsService.checkLike(id, clientId)
            setIsLiked(likeStatus.isLiked || false)
          } catch (err) {
            console.error('Erreur vérification like:', err)
          }

          // Vérifier le statut d'abonnement (localStorage)
          if (productData?.client?.id) {
            const subKey = `sub_${clientId}_${productData.client.id}`
            setIsSubscribed(localStorage.getItem(subKey) === 'true')
          }
        }

        // Commentaires (public)
        try {
          const commentsData = await likesCommentsService.getComments(id)
          if (commentsData.success) {
            setComments(commentsData.comments || [])
          }
        } catch (err) {
          console.error('Erreur commentaires:', err)
        }

        // Récupérer les produits similaires
        if (productData?.category_id) {
          try {
            const data = await publicApiClient.get('/posts', {
              params: { post_categorie_id: productData.category_id, type: 2, status: 2 }
            })
            const items = data.data || data.posts || (Array.isArray(data) ? data : [])
            const related = items
              .filter(p => p.post_id !== productData.id)
              .slice(0, 20)
              .map(p => ({
                id:       p.post_id,
                name:     p.libele,
                ...resolvePricing(p),
                image:    (() => { const f = p.fichiers?.find(f => f.is_main) ?? p.fichiers?.[0]; return f?.chemin ? mediaUrl(f.chemin) : null })(),
                views:    p.vues || 0,
                likes:    p.post_likes_count ?? p.likes ?? 0,
                comments: p.post_comments_count ?? p.comments ?? 0,
                date:     p.created_at ? new Date(p.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '',
                etat_appareil: p.etat_appareil || '',
              }))
            setRelatedProducts(related)
          } catch {}
        }
      } catch (err) {
        console.error('Erreur lors du chargement du produit:', err)
        setProduct(null)
      } finally {
        setLoading(false)
      }
    }

    if (id) {
      fetchProduct()
    }
  }, [id])

  // Incrément de vue — effet séparé avec guard pour éviter le double-appel (React StrictMode)
  useEffect(() => {
    if (!id || incrementedIdRef.current === id) return
    incrementedIdRef.current = id
    publicApiClient.post(`/posts/${id}/view`).catch(() => {})
  }, [id])

  const handleLike = async () => {
    // Like libre sans connexion — optimiste pour les invités
    if (!isAuthenticated) {
      setIsLiked(prev => !prev)
      setLikes(prev => isLiked ? prev - 1 : prev + 1)
      return
    }
    const clientId = user?.id || user?.client_id
    try {
      if (isLiked) {
        const result = await likesCommentsService.unlikePost(id, clientId)
        if (result.success) { setLikes(result.likes); setIsLiked(false) }
      } else {
        const result = await likesCommentsService.likePost(id, clientId)
        if (result.success) { setLikes(result.likes); setIsLiked(true) }
      }
    } catch (error) {
      console.error('Erreur lors du like:', error)
    }
  }

  const handleAddComment = async () => {
    if (!newComment.trim()) return
    // Connexion obligatoire pour commenter
    if (!isAuthenticated) {
      navigate('/login')
      return
    }
    const clientId = user?.id || user?.client_id
    setLoadingComments(true)
    try {
      const result = await likesCommentsService.addComment(id, newComment, clientId)
      if (result.success) {
        setComments([result.comment, ...comments])
        setNewComment('')
        if (product) setProduct({...product, comments: (product.comments || 0) + 1})
      }
    } catch (error) {
      console.error('Erreur lors de l\'ajout du commentaire:', error)
    } finally {
      setLoadingComments(false)
    }
  }

  const handleSubscribe = async () => {
    if (!isAuthenticated) { navigate('/login'); return }
    const clientId = user?.id || user?.client_id
    const abonneId = product.client?.id
    if (!abonneId) { toast.error('Impossible de trouver l\'ID du vendeur'); return }
    try {
      await apiClient.post('/subscription/subscribe', {
        action: 'toggle',
        client_id: clientId,
        abonne_id: abonneId
      })
      const newState = !isSubscribed
      setIsSubscribed(newState)
      setSubscribersCount(prev => newState ? prev + 1 : prev - 1)
      localStorage.setItem(`sub_${clientId}_${abonneId}`, newState)
      toast.success(newState ? 'Abonné avec succès' : 'Désabonné avec succès')
    } catch {
      toast.error('Erreur lors de l\'abonnement')
    }
  }

  const handleMessage = () => {
    if (!isAuthenticated) { navigate('/login'); return }
    navigate('/messages', {
      state: {
        openConversationWith: {
          id:     product.client?.id,
          name:   product.client?.name,
          phone:  product.client?.phone,
          avatar: product.client?.photo,
          article: { name: product.name, url: `/product/${encodedId}`, image: product.image },
        }
      }
    })
  }

  const handleWhatsApp = () => {
    const phone = product.client?.phone
    if (!phone) return
    let digits = phone.replace(/\D/g, '')
    // Ajoute l'indicatif +225 si le numéro commence par 0 (Côte d'Ivoire)
    if (digits.startsWith('0') && digits.length <= 10) digits = '225' + digits.slice(1)
    window.open(`https://wa.me/${digits}`, '_blank')
  }

  const handleShare = async () => {
    const url = window.location.href
    try {
      if (navigator.share) {
        await navigator.share({ title: product.name, url })
      } else {
        await navigator.clipboard.writeText(url)
        toast.success('Lien copié')
      }
    } catch { /* partage annulé */ }
  }

  if (loading) {
    return (
      <div className="home-font min-h-screen page-pattern">
        <div className="w-[94%] md:w-[80%] mx-auto py-8 grid lg:grid-cols-[minmax(0,1fr)_380px] gap-8">
          <div className="space-y-4">
            <div className="h-[320px] sm:h-[420px] lg:h-[500px] rounded-md skeleton-shimmer" />
            <div className="flex gap-3">{[1, 2, 3, 4].map(i => <div key={i} className="h-20 w-20 rounded-md skeleton-shimmer" />)}</div>
          </div>
          <div className="space-y-4">
            <div className="h-8 w-3/4 rounded-md skeleton-shimmer" />
            <div className="h-10 w-1/2 rounded-md skeleton-shimmer" />
            <div className="h-48 rounded-md skeleton-shimmer" />
          </div>
        </div>
      </div>
    )
  }

  if (!product) {
    return (
      <div className="home-font min-h-screen page-pattern flex items-center justify-center px-4">
        <div className="text-center">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-md" style={GOLD_PILL}>
            <Svg d={PATHS.search} className="w-7 h-7" />
          </span>
          <h2 className="mt-5 text-2xl font-extrabold text-[#002A4E]">Produit introuvable</h2>
          <p className="mt-2 text-slate-500">Cette annonce n&apos;existe plus ou a expiré.</p>
          <button
            onClick={() => navigate('/kudhi-market')}
            className="mt-6 rounded-md bg-[#FFD700] px-6 py-3 font-bold text-[#002A4E] transition-colors hover:bg-[#F5C800]"
          >
            Retour au Market
          </button>
        </div>
      </div>
    )
  }

  // Calculer le pourcentage de réduction
  const priceNum = typeof product.price === 'string' ? parseFloat(product.price.replace(/[^0-9]/g, '')) : product.price
  const oldPriceNum = product.oldPrice ? (typeof product.oldPrice === 'string' ? parseFloat(product.oldPrice.replace(/[^0-9]/g, '')) : product.oldPrice) : 0
  const discount = oldPriceNum > 0 ? Math.round((1 - priceNum / oldPriceNum) * 100) : 0

  const gallery = product.images?.length ? product.images : [product.image]
  const currentImage = gallery[selectedImage] || product.image
  const isOwner = user && (user?.id === product.client?.id || user?.client_id === product.client?.id)
  const sellerName = product.client?.name || 'Vendeur'
  const sellerInitials = sellerName.split(' ').map(w => w[0]).filter(Boolean).join('').slice(0, 2).toUpperCase()

  return (
    <div className="home-font min-h-screen page-pattern">
      <div className="w-[94%] md:w-[80%] mx-auto py-6 lg:py-8">

        {/* Fil d'Ariane */}
        <nav className="mb-5 flex items-center gap-2 text-sm text-slate-500">
          <button onClick={() => navigate(-1)} className="flex h-9 w-9 items-center justify-center rounded-md bg-white text-[#002A4E] ring-1 ring-slate-200 transition-colors hover:ring-amber-300" aria-label="Retour">
            <Svg d={PATHS.chevLeft} className="w-4 h-4" stroke={2.2} />
          </button>
          <button onClick={() => navigate('/kudhi-market')} className="font-medium hover:text-[#002A4E]">KUDHI Market</button>
          <Svg d={PATHS.chevRight} className="w-3.5 h-3.5 text-slate-300" />
          <span className="truncate font-semibold text-[#002A4E]">{product.name}</span>
        </nav>

        <div className="grid grid-cols-[minmax(0,1fr)] lg:grid-cols-[minmax(0,1fr)_380px] gap-6 lg:gap-8">

          {/* ===== Galerie ===== */}
          <section className="lg:col-start-1">
            <div className="rounded-md bg-white p-3 ring-1 ring-slate-200/70">
              <div className="relative h-[320px] sm:h-[420px] lg:h-[max(460px,calc(100vh-380px))] flex items-center justify-center overflow-hidden rounded-md bg-white">
                <img key={currentImage} src={currentImage} alt={product.name} className="h-full w-full object-contain animate-fade-in" />

                <div className="absolute left-3 top-3 flex flex-col items-start gap-2">
                  {discount > 0 && <span className="rounded-md bg-red-600 px-2.5 py-1 text-sm font-extrabold text-white shadow-md">-{discount}%</span>}
                  {product.etat_appareil_label && (
                    <span className={`rounded-md px-2.5 py-1 text-xs font-bold shadow-md ${conditionClass(product.etat_appareil)}`}>{product.etat_appareil_label}</span>
                  )}
                </div>

                {gallery.length > 1 && (
                  <>
                    <GalleryArrow side="left" onClick={() => setSelectedImage(i => (i - 1 + gallery.length) % gallery.length)} />
                    <GalleryArrow side="right" onClick={() => setSelectedImage(i => (i + 1) % gallery.length)} />
                    <span className="absolute bottom-3 right-3 rounded-md bg-black/55 px-2.5 py-1 text-xs font-semibold text-white backdrop-blur-sm tabular-nums">
                      {selectedImage + 1} / {gallery.length}
                    </span>
                  </>
                )}
              </div>

              {gallery.length > 0 && (
                <div className="mt-3 flex items-center gap-4 rounded-md bg-slate-50 p-2.5">
                  <div className="flex min-w-0 flex-1 gap-3 overflow-x-auto scrollbar-hide p-1.5">
                    {gallery.map((img, index) => {
                      const active = selectedImage === index
                      return (
                        <button
                          key={index}
                          onClick={() => setSelectedImage(index)}
                          aria-label={`Photo ${index + 1}`}
                          className={`group relative h-16 w-16 sm:h-[72px] sm:w-[72px] flex-shrink-0 overflow-hidden rounded-md bg-white transition-all ${
                            active
                              ? 'ring-2 ring-[#FFD700] ring-offset-2 ring-offset-slate-50 shadow-md shadow-amber-300/40'
                              : 'ring-1 ring-slate-200 hover:ring-amber-300 hover:-translate-y-0.5'
                          }`}
                        >
                          <img
                            src={img || product.image}
                            alt={`${product.name} ${index + 1}`}
                            className={`h-full w-full object-cover transition-all duration-300 ${active ? '' : 'opacity-60 grayscale-[30%] group-hover:opacity-100 group-hover:grayscale-0'}`}
                          />
                          <span className={`absolute bottom-1 left-1 rounded px-1 text-[10px] font-bold tabular-nums ${active ? 'bg-[#FFD700] text-[#002A4E]' : 'bg-black/50 text-white'}`}>
                            {index + 1}
                          </span>
                        </button>
                      )
                    })}
                  </div>
                  <div className="hidden sm:flex flex-shrink-0 items-center gap-2.5 border-l border-slate-200 pl-4 pr-1">
                    <span className="flex h-9 w-9 items-center justify-center rounded-md bg-white text-[#B8860B] ring-1 ring-slate-200">
                      <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A1.5 1.5 0 0021.75 19.5V4.5A1.5 1.5 0 0020.25 3H3.75A1.5 1.5 0 002.25 4.5v15A1.5 1.5 0 003.75 21zM15 8.25h.008v.008H15V8.25z" /></svg>
                    </span>
                    <div className="leading-tight">
                      <p className="text-sm font-bold text-[#002A4E] tabular-nums">{gallery.length} photo{gallery.length > 1 ? 's' : ''}</p>
                      <p className="text-xs text-slate-400">{gallery.length > 1 ? 'Cliquez pour afficher' : 'Photo du vendeur'}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* ===== Carte d'achat ===== */}
          <aside className="lg:col-start-2 lg:row-start-1 lg:row-span-2 lg:sticky lg:top-28 self-start space-y-4">
            <div className="rounded-md bg-white p-5 sm:p-6 ring-1 ring-slate-200/70">
              <h1 className="text-xl sm:text-2xl font-extrabold leading-tight tracking-tight text-[#002A4E]">{product.name}</h1>

              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
                <span className="inline-flex items-center gap-1.5"><Svg d={PATHS.eye} className="w-4 h-4" />{product.views} vue{product.views > 1 ? 's' : ''}</span>
                <span className="inline-flex items-center gap-1.5"><Svg d={PATHS.heart} className="w-4 h-4" />{likes}</span>
                {product.date && <span className="inline-flex items-center gap-1.5"><Svg d={PATHS.clock} className="w-4 h-4" />{product.date}</span>}
              </div>

              <div className="mt-5 rounded-md bg-slate-50 p-4">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <span className={`text-3xl font-extrabold tracking-tight ${discount > 0 ? 'text-red-600' : 'text-[#002A4E]'}`}>
                    {formatPrice(product.price) || 'Prix sur demande'}
                  </span>
                  {product.oldPrice && <span className="text-base text-slate-400 line-through">{formatPrice(product.oldPrice)}</span>}
                </div>
                {discount > 0 && (
                  <p className="mt-1.5 text-sm font-semibold text-emerald-600">
                    Vous économisez {formatPrice(oldPriceNum - priceNum)} (-{discount}%)
                  </p>
                )}
              </div>

              {/* Actions de contact */}
              {!isOwner && product.client && (
                <div className="mt-5 space-y-2.5">
                  <button
                    onClick={() => { if (product.client?.phone) window.location.href = `tel:${product.client.phone}` }}
                    className="flex w-full items-center justify-center gap-2 rounded-md bg-[#FFD700] py-3.5 font-bold text-[#002A4E] shadow-sm shadow-amber-300/60 transition-all hover:-translate-y-0.5 hover:bg-[#F5C800]"
                  >
                    <Svg d={PATHS.phone} className="w-5 h-5" stroke={2} />
                    Appeler {product.client?.phone ? <span className="font-semibold opacity-75">· {product.client.phone}</span> : null}
                  </button>
                  <div className="grid grid-cols-2 gap-2.5">
                    <button
                      onClick={handleMessage}
                      className="flex items-center justify-center gap-2 rounded-md bg-[#002A4E] py-3 font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-[#003866]"
                    >
                      <Svg d={PATHS.message} className="w-5 h-5" />
                      Message
                    </button>
                    <button
                      onClick={handleWhatsApp}
                      className="flex items-center justify-center gap-2 rounded-md bg-[#25D366] py-3 font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-[#1ebe5b]"
                    >
                      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d={WHATSAPP_PATH} /></svg>
                      WhatsApp
                    </button>
                  </div>
                </div>
              )}

              {/* J'aime + Partager */}
              <div className="mt-3 grid grid-cols-2 gap-2.5">
                <button
                  onClick={handleLike}
                  className={`flex items-center justify-center gap-2 rounded-md py-2.5 text-sm font-semibold ring-1 transition-colors ${
                    isLiked ? 'bg-red-50 text-red-600 ring-red-200' : 'bg-white text-slate-600 ring-slate-200 hover:text-red-600 hover:ring-red-200'
                  }`}
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill={isLiked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d={PATHS.heart} /></svg>
                  {isLiked ? 'Aimé' : 'J\'aime'}
                </button>
                <button
                  onClick={handleShare}
                  className="flex items-center justify-center gap-2 rounded-md bg-white py-2.5 text-sm font-semibold text-slate-600 ring-1 ring-slate-200 transition-colors hover:text-[#002A4E] hover:ring-amber-300"
                >
                  <Svg d={PATHS.share} className="w-5 h-5" />
                  Partager
                </button>
              </div>
            </div>

            {/* Vendeur */}
            {product.client && (
              <div className="rounded-md bg-white p-5 ring-1 ring-slate-200/70">
                <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-[#B8860B]"><Svg d={PATHS.user} className="w-4 h-4" stroke={2} />Vendeur</p>
                <div className="mt-3 flex items-center gap-3">
                  {product.client.photo ? (
                    <img
                      src={product.client.photo}
                      alt={sellerName}
                      className="h-12 w-12 flex-shrink-0 rounded-full object-cover ring-2 ring-[#FFD700]"
                      onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex' }}
                    />
                  ) : null}
                  <span
                    className="h-12 w-12 flex-shrink-0 items-center justify-center rounded-full text-base font-bold"
                    style={{ ...GOLD_PILL, display: product.client.photo ? 'none' : 'flex' }}
                  >
                    {sellerInitials}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-bold text-[#002A4E]">{sellerName}</p>
                    <p className="truncate text-sm text-slate-500">{product.client.phone || 'Téléphone non disponible'}</p>
                  </div>
                  {!isOwner && (
                    <button
                      onClick={handleSubscribe}
                      className={`flex-shrink-0 rounded-md px-4 py-2 text-sm font-bold transition-colors ${
                        isSubscribed ? 'bg-slate-100 text-slate-600 hover:bg-slate-200' : 'bg-[#002A4E] text-white hover:bg-[#003866]'
                      }`}
                    >
                      <span className="inline-flex items-center justify-center gap-1.5"><Svg d={isSubscribed ? PATHS.check : PATHS.userPlus} className="w-4 h-4" stroke={2} />{isSubscribed ? 'Abonné' : 'S\'abonner'}</span>
                    </button>
                  )}
                </div>
                {product.location && (
                  <div className="mt-4 flex items-start gap-2 rounded-md bg-[#FFF8D6] p-3 text-sm text-[#002A4E]">
                    <Svg d={PATHS.pin} className="mt-0.5 w-4 h-4 flex-shrink-0 text-[#B8860B]" />
                    <span className="break-words">{product.location.replace(/^https?:\/\//i, '').replace(/\/$/, '')}</span>
                  </div>
                )}
              </div>
            )}

            {/* Conseils de sécurité */}
            <div className="rounded-md bg-[#002A4E] p-5 text-white">
              <p className="flex items-center gap-2 font-bold">
                <Svg d={PATHS.shield} className="w-5 h-5 text-[#FFD700]" />
                Achetez en toute sécurité
              </p>
              <ul className="mt-3 space-y-1.5 text-sm text-white/75">
                <li>• Rencontrez le vendeur dans un lieu public</li>
                <li>• Vérifiez le produit avant de payer</li>
                <li>• Ne payez jamais à l&apos;avance sans garantie</li>
              </ul>
            </div>
          </aside>

          {/* ===== Détails ===== */}
          <section className="lg:col-start-1">
            <div className="rounded-md bg-white ring-1 ring-slate-200/70">
              {/* Onglets */}
              <div className="flex gap-1 overflow-x-auto scrollbar-hide border-b border-slate-100 px-3 sm:px-5">
                {[
                  ['description', 'Description', 'doc'],
                  ...(product.specs?.length > 0 ? [['specs', 'Caractéristiques', 'list']] : []),
                  ['comments', `Commentaires (${comments.length})`, 'message'],
                ].map(([key, label, icon]) => (
                  <button
                    key={key}
                    onClick={() => setDetailTab(key)}
                    className={`relative inline-flex flex-shrink-0 items-center gap-2 px-3 py-4 text-sm sm:text-base font-bold transition-colors ${
                      detailTab === key ? 'text-[#002A4E]' : 'text-slate-400 hover:text-[#002A4E]'
                    }`}
                  >
                    <span className={`flex h-7 w-7 items-center justify-center rounded-md ${detailTab === key ? 'bg-[#FFD700] text-[#002A4E]' : 'bg-slate-100 text-slate-400'}`}>
                      <Svg d={PATHS[icon]} className="w-4 h-4" stroke={2} />
                    </span>
                    {label}
                    {detailTab === key && <span className="absolute inset-x-0 -bottom-px h-[3px] rounded-full bg-[#F5B700]" />}
                  </button>
                ))}
              </div>

              <div className="p-5 sm:p-7">
                {detailTab === 'description' && (
                  product.description
                    ? <p className="whitespace-pre-line leading-relaxed text-slate-600">{product.description}</p>
                    : <p className="text-sm text-slate-400">Aucune description pour cette annonce.</p>
                )}

                {detailTab === 'specs' && product.specs?.length > 0 && (
                  <dl className="grid sm:grid-cols-2 gap-x-8">
                    {product.specs.map(([label, value]) => (
                      <div key={label} className="flex items-center justify-between gap-4 border-b border-slate-100 py-3">
                        <dt className="text-sm text-slate-500">{label}</dt>
                        <dd className="text-right text-sm font-semibold text-[#002A4E]">{value}</dd>
                      </div>
                    ))}
                  </dl>
                )}

                {detailTab === 'comments' && (
                  <div className="space-y-5">
                    {isAuthenticated ? (
                      <div className="flex gap-3">
                        {(() => {
                          const rawPhoto = user?.photo_profil || user?.photo || null
                          const photoUrl = rawPhoto
                            ? rawPhoto.startsWith('http') ? rawPhoto
                              : rawPhoto.startsWith('/storage/') ? `${storageURL.replace(/\/storage$/, '')}${rawPhoto}`
                              : mediaUrl(rawPhoto)
                            : null
                          const initials = [user?.nom || user?.name || '', user?.prenom || '']
                            .map(s => s.charAt(0)).filter(Boolean).join('').toUpperCase() || 'U'
                          return (
                            <div className="relative h-10 w-10 flex-shrink-0">
                              <span className="flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold" style={GOLD_PILL}>{initials}</span>
                              {photoUrl && (
                                <img src={photoUrl} alt="" className="absolute inset-0 h-10 w-10 rounded-full object-cover" onError={e => { e.target.style.display = 'none' }} />
                              )}
                            </div>
                          )
                        })()}
                        <div className="flex-1">
                          <textarea
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            placeholder="Posez une question ou laissez un avis…"
                            rows="3"
                            className="w-full resize-none rounded-md border border-slate-200 bg-slate-50/70 p-3 text-sm text-[#002A4E] placeholder:text-slate-400 focus:border-amber-300 focus:bg-white focus:outline-none focus:ring-4 focus:ring-amber-100"
                          />
                          <div className="mt-2 flex justify-end">
                            <button
                              onClick={handleAddComment}
                              disabled={loadingComments || !newComment.trim()}
                              className="rounded-md bg-[#FFD700] px-5 py-2.5 text-sm font-bold text-[#002A4E] transition-colors hover:bg-[#F5C800] disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {loadingComments ? 'Publication…' : 'Publier'}
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 rounded-md bg-[#FFF8D6] p-4 text-sm text-[#002A4E]">
                        <Svg d={PATHS.message} className="w-5 h-5 flex-shrink-0 text-[#B8860B]" />
                        <span>
                          <button onClick={() => navigate('/login')} className="font-bold underline underline-offset-2">Connectez-vous</button>
                          {' '}pour laisser un commentaire.
                        </span>
                      </div>
                    )}

                    {comments.length === 0 ? (
                      <p className="py-6 text-center text-sm text-slate-400">Aucun commentaire pour le moment. Soyez le premier !</p>
                    ) : (
                      <div className="divide-y divide-slate-100">
                        {comments.map((comment) => {
                          const clientName = comment.client?.name || comment.author || 'Anonyme'
                          const clientPhoto = comment.client?.photo
                            ? (comment.client.photo.startsWith('http') ? comment.client.photo : mediaUrl(comment.client.photo))
                            : null
                          const initials = clientName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
                          const commentText = comment.comment_text || comment.text || ''
                          const commentDate = comment.created_at
                            ? new Date(comment.created_at).toLocaleDateString('fr-FR')
                            : comment.date || ''
                          return (
                            <div key={comment.id} className="flex gap-3 py-4">
                              {clientPhoto ? (
                                <img src={clientPhoto} alt={clientName} className="h-10 w-10 flex-shrink-0 rounded-full object-cover" />
                              ) : (
                                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold" style={GOLD_PILL}>{initials}</span>
                              )}
                              <div className="min-w-0 flex-1">
                                <div className="flex items-baseline gap-2">
                                  <span className="font-bold text-[#002A4E]">{clientName}</span>
                                  <span className="text-xs text-slate-400">{commentDate}</span>
                                </div>
                                <p className="mt-1 text-sm leading-relaxed text-slate-600">{commentText}</p>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </section>
        </div>

        {/* ===== Articles similaires ===== */}
        {relatedProducts.length > 0 && (() => {
          const totalPages = Math.ceil(relatedProducts.length / perView)
          const cardWidthPct = 100 / perView
          return (
            <section className="mt-12">
              <div className="mb-5 flex items-end justify-between gap-4">
                <div>
                  <p className="text-sm font-bold uppercase tracking-widest text-[#B8860B]">Vous aimerez aussi</p>
                  <SectionTitle icon="grid" className="mt-1 text-2xl tracking-tight">Articles similaires</SectionTitle>
                </div>
                {totalPages > 1 && (
                  <div className="flex gap-2">
                    <GalleryArrow inline side="left" disabled={relatedPage === 0} onClick={() => setRelatedPage(p => Math.max(0, p - 1))} />
                    <GalleryArrow inline side="right" disabled={relatedPage >= totalPages - 1} onClick={() => setRelatedPage(p => Math.min(totalPages - 1, p + 1))} />
                  </div>
                )}
              </div>

              <div className="overflow-hidden -mx-1.5">
                <div className="flex transition-transform duration-300 ease-in-out" style={{ transform: `translateX(-${relatedPage * 100}%)` }}>
                  {relatedProducts.map(p => {
                    const pn  = parseFloat(String(p.price).replace(/[^0-9.]/g, '')) || 0
                    const opn = parseFloat(String(p.oldPrice).replace(/[^0-9.]/g, '')) || 0
                    const disc = opn > pn ? Math.round((1 - pn / opn) * 100) : 0
                    return (
                      <div key={p.id} className="flex-shrink-0 px-1.5" style={{ width: `${cardWidthPct}%` }}>
                        <button
                          onClick={() => { navigate(`/product/${encodeId(p.id)}`); window.scrollTo(0, 0) }}
                          className="group block w-full overflow-hidden rounded-md bg-white text-left ring-1 ring-slate-200/70 transition-all hover:-translate-y-1 hover:shadow-lg"
                        >
                          <div className="relative aspect-square overflow-hidden bg-slate-100">
                            <img src={p.image} alt={p.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" onError={e => { e.target.style.display = 'none' }} />
                            {disc > 0 && <span className="absolute left-2 top-2 rounded-md bg-red-600 px-1.5 py-0.5 text-xs font-bold text-white">-{disc}%</span>}
                            {p.etat_appareil && (
                              <span className={`absolute right-2 top-2 rounded-md px-1.5 py-0.5 text-[11px] font-bold ${conditionClass(p.etat_appareil)}`}>
                                {CONDITION_LABELS[p.etat_appareil] || p.etat_appareil}
                              </span>
                            )}
                          </div>
                          <div className="p-3">
                            <h4 className="text-sm font-semibold text-[#002A4E] line-clamp-1">{p.name}</h4>
                            <div className="mt-1 flex flex-wrap items-baseline gap-x-2">
                              <span className={`text-sm font-extrabold ${disc > 0 ? 'text-red-600' : 'text-[#002A4E]'}`}>{formatPrice(p.price)}</span>
                              {p.oldPrice && <span className="text-xs text-slate-400 line-through">{formatPrice(p.oldPrice)}</span>}
                            </div>
                            <div className="mt-2 flex items-center gap-3 text-xs text-slate-400">
                              <span className="inline-flex items-center gap-1"><Svg d={PATHS.eye} className="w-3.5 h-3.5" />{p.views}</span>
                              <span className="inline-flex items-center gap-1"><Svg d={PATHS.heart} className="w-3.5 h-3.5" />{p.likes}</span>
                              <span className="ml-auto">{p.date}</span>
                            </div>
                          </div>
                        </button>
                      </div>
                    )
                  })}
                </div>
              </div>
            </section>
          )
        })()}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Éléments d'interface de la page détail
// ---------------------------------------------------------------------------

const GOLD_PILL = { background: 'linear-gradient(135deg, #FFE45C 0%, #FFD700 50%, #F5B700 100%)', color: '#002A4E' }

const PATHS = {
  doc: 'M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z',
  userPlus: 'M19 7.5v3m0 0v3m0-3h3m-3 0h-3m-2.25-4.125a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zM4 19.235v-.11a6.375 6.375 0 0112.75 0v.109A12.318 12.318 0 0110.374 21c-2.331 0-4.512-.645-6.374-1.766z',
  check: 'M4.5 12.75l6 6 9-13.5',
  list: 'M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM3.75 12h.007v.008H3.75V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm-.375 5.25h.007v.008H3.75v-.008zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z',
  user: 'M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z',
  grid: 'M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25a2.25 2.25 0 01-2.25-2.25v-2.25z',
  eye: 'M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z M15 12a3 3 0 11-6 0 3 3 0 016 0z',
  heart: 'M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z',
  message: 'M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z',
  phone: 'M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z',
  share: 'M7.217 10.907a2.25 2.25 0 100 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186l9.566-5.314m-9.566 7.5l9.566 5.314m0 0a2.25 2.25 0 103.935 2.186 2.25 2.25 0 00-3.935-2.186zm0-12.814a2.25 2.25 0 103.933-2.185 2.25 2.25 0 00-3.933 2.185z',
  clock: 'M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z',
  pin: 'M15 10.5a3 3 0 11-6 0 3 3 0 016 0z M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z',
  shield: 'M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z',
  search: 'M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z',
  chevLeft: 'M15.75 19.5L8.25 12l7.5-7.5',
  chevRight: 'M8.25 4.5l7.5 7.5-7.5 7.5',
}

const WHATSAPP_PATH = 'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z'

// Couleur du badge selon l'état de l'appareil
const conditionClass = (etat) => ({
  neuf: 'bg-blue-600 text-white',
  scelle: 'bg-green-600 text-white',
  quasi_neuf: 'bg-teal-500 text-white',
  excellent: 'bg-indigo-500 text-white',
  bon: 'bg-yellow-400 text-[#002A4E]',
  moyen: 'bg-orange-500 text-white',
  usage: 'bg-red-500 text-white',
  reconditionne: 'bg-purple-500 text-white',
}[etat] || 'bg-slate-600 text-white')

const Svg = ({ d, className = 'w-5 h-5', stroke = 1.8 }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke} aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d={d} />
  </svg>
)

const GalleryArrow = ({ side, onClick, inline = false, disabled = false }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    aria-label={side === 'left' ? 'Précédent' : 'Suivant'}
    className={`${inline ? '' : `absolute top-1/2 -translate-y-1/2 ${side === 'left' ? 'left-3' : 'right-3'}`} flex h-10 w-10 items-center justify-center rounded-md bg-white/90 text-[#002A4E] shadow-md ring-1 ring-slate-200 transition-all hover:bg-white hover:ring-amber-300 disabled:cursor-not-allowed disabled:opacity-40`}
  >
    <Svg d={side === 'left' ? PATHS.chevLeft : PATHS.chevRight} className="w-5 h-5" stroke={2.2} />
  </button>
)

// Titre de section avec pastille d'icône
const SectionTitle = ({ icon, children, className = '' }) => (
  <h2 className={`flex items-center gap-2.5 text-lg font-extrabold text-[#002A4E] ${className}`}>
    <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md" style={GOLD_PILL}>
      <Svg d={PATHS[icon]} className="w-4 h-4" stroke={2} />
    </span>
    {children}
  </h2>
)

export default ProductDetailPage